import {
  BulkMemberTransferRequestFormSchema,
  BulkMemberTransferRequestFormType,
} from '@act/shared/data-transfer-objects';
import { zodResolver } from '@hookform/resolvers/zod';
import { DateTime } from 'luxon';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { memberTransferRequests } from 'redux/requests/memberTransfer.requests';
import { selectActingUser } from 'redux/slices/actingUserSlice.slice';
import { membersAppSelectors } from 'redux/slices/memberSlice.slice';
import {
  createBulkMemberTransferRequestSuccess,
  getMemberTransferReasons,
  memberTransferRequestSelectors,
} from 'redux/slices/memberTransferRequestSlice.slice';
import {
  loadTeamHierarchy,
  selectHierarchy,
  selectHierarchyLoadingStatus,
  userAppSelectors,
} from 'redux/slices/usersSlice.slice';
import { useAppDispatch, useAppSelector } from 'redux/storeHooks';
import { ensureTeamAllViewable } from 'redux/thunks/referenceData.thunk';
import { useApiCall } from 'redux/utils/apiHook.hook';
import { useToast } from '../../app/ReusableToast';
import { buildBulkTransferConfirmationSummary } from '../../member-index/buildBulkTransferConfirmationSummary.util';
import { getEligibleBulkTransferGuides } from '../../member-index/getEligibleBulkTransferGuides.util';
import { BulkTransferTablePrefill } from '../../member-index/resolveBulkTransferTablePrefill.util';
import { shouldSuggestBulkMemberTransfer } from '../../member-index/shouldSuggestBulkMemberTransfer.util';
import { createBulkMemberTransferRequestFormConfig } from '../BulkMemberTransferRequestFormConfig';
import { SCHEDULED_EVENT_DATE_FORMAT } from '../scheduledEventForm.util';
import useUnsavedChangesDialog from './useUnsavedChangesDialog.hook';

type BulkTransferDrawerStep = 'form' | 'confirmation';

interface UseBulkMemberTransferRequestFormParams {
  open: boolean;
  setOpen: (open: boolean) => void;
  tablePrefill?: BulkTransferTablePrefill;
}

/**
 * Orchestrates the page-scoped bulk transfer drawer.
 *
 * @param params - Drawer state and optional member-table prefill.
 * @returns Form state, display data, and drawer actions.
 */
export function useBulkMemberTransferRequestForm({
  open,
  setOpen,
  tablePrefill,
}: UseBulkMemberTransferRequestFormParams) {
  const dispatch = useAppDispatch();
  const toast = useToast();
  const handleUnsavedChanges = useUnsavedChangesDialog();
  const actingUser = useAppSelector(selectActingUser);
  const guideMap = userAppSelectors.useSelectUsersMap();
  const paginatedMembers = membersAppSelectors.useSelectAllPaginatedMembers();
  const memberTransferReasons =
    memberTransferRequestSelectors.useSelectMemberTransferReasons();
  const reasonsStatus =
    memberTransferRequestSelectors.useSelectMemberTransferSliceStatuses()
      .reasonsStatus;
  const viewableGuides = userAppSelectors.useSelectCurrentGuides();
  const hierarchyLoadingStatus = useAppSelector(selectHierarchyLoadingStatus);
  const teamHierarchy = useAppSelector(selectHierarchy);
  const [drawerStep, setDrawerStep] =
    useState<BulkTransferDrawerStep>('form');
  const [confirmationFormValues, setConfirmationFormValues] =
    useState<BulkMemberTransferRequestFormType | null>(null);
  const previousOriginatingGuideId = useRef<string | null>(null);

  const defaultFormValues = useMemo(
    () => ({
      memberIds: tablePrefill?.memberIds ?? [],
      originatingGuideId: tablePrefill?.originatingGuideId ?? '',
      receivingGuideId: '',
      creatorId: actingUser?.id,
      transferReasonId: 0,
      commentToReceivingGuide: '',
      stringDetail: '',
      numericDetail: null,
      numericDetailByMemberId: {},
      timestampDetail: DateTime.now().toFormat(SCHEDULED_EVENT_DATE_FORMAT),
    }),
    [actingUser?.id, tablePrefill?.memberIds, tablePrefill?.originatingGuideId]
  );
  const form = useForm<BulkMemberTransferRequestFormType>({
    mode: 'onChange',
    defaultValues: defaultFormValues,
    resolver: zodResolver(BulkMemberTransferRequestFormSchema),
  });
  const {
    reset,
    setValue,
    watch,
    formState,
  } = form;
  const originatingGuideId = watch('originatingGuideId') || '';
  const transferReasonId = Number(watch('transferReasonId'));
  const selectedMemberIds = watch('memberIds') ?? [];

  const guideNameById = useMemo(
    () =>
      Object.fromEntries(
        Object.entries(guideMap).map(([guideId, guide]) => [
          guideId,
          guide?.name,
        ])
      ),
    [guideMap]
  );
  const originatingGuideOptions = useMemo(
    () =>
      getEligibleBulkTransferGuides(
        paginatedMembers,
        guideNameById,
        actingUser
      ).map(guide => ({
        label: guide.guideName,
        value: guide.guideId,
      })),
    [actingUser, guideNameById, paginatedMembers]
  );
  const caseloadMembers = useMemo(
    () =>
      paginatedMembers.filter(
        member =>
          member.assignedGuideId === originatingGuideId &&
          shouldSuggestBulkMemberTransfer(member, actingUser)
      ),
    [actingUser, originatingGuideId, paginatedMembers]
  );
  const selectedTransferReason = useMemo(
    () =>
      memberTransferReasons.find(reason => reason.id === transferReasonId) ??
      null,
    [memberTransferReasons, transferReasonId]
  );
  const membersById = useMemo(
    () =>
      Object.fromEntries(
        paginatedMembers.map(member => [member.id, member])
      ),
    [paginatedMembers]
  );
  const addressOptionsByMemberId = useMemo(
    () =>
      Object.fromEntries(
        caseloadMembers.map(member => [
          member.id,
          {
            memberName: member.name,
            options: (member.addresses ?? [])
              .filter(address => !address.deletedAt)
              .map(address => ({
                value: address.id,
                label: [
                  address.line1,
                  address.line2,
                  address.city,
                  address.state,
                  address.zip,
                ]
                  .filter(Boolean)
                  .join(', '),
              })),
          },
        ])
      ),
    [caseloadMembers]
  );
  const confirmationSummary = useMemo(
    () =>
      confirmationFormValues
        ? buildBulkTransferConfirmationSummary({
            formValues: confirmationFormValues,
            selectedTransferReason,
            membersById,
            guideNameById,
          })
        : null,
    [
      confirmationFormValues,
      guideNameById,
      membersById,
      selectedTransferReason,
    ]
  );
  const formConfig = createBulkMemberTransferRequestFormConfig({
    originatingGuideOptions,
    caseloadMemberOptions: caseloadMembers.map(member => ({
      label: member.name,
      value: member.id,
    })),
    receivingGuideOptions: [...(viewableGuides ?? [])]
      .filter(guide => guide.id !== originatingGuideId)
      .map(guide => ({ label: guide.name, value: guide.id })),
    memberTransferReasons,
    selectedTransferReason,
    selectedMemberIds,
    addressOptionsByMemberId,
  });
  const [
    createBulkResult,
    createBulkLoading,
    createBulkMemberTransferRequest,
  ] = useApiCall(memberTransferRequests.createBulkMemberTransferRequest);

  useEffect(() => {
    if (!open) {
      previousOriginatingGuideId.current = null;
      setDrawerStep('form');
      setConfirmationFormValues(null);
      return;
    }
    reset(defaultFormValues);
    previousOriginatingGuideId.current =
      tablePrefill?.originatingGuideId ?? null;
  }, [defaultFormValues, open, reset, tablePrefill?.originatingGuideId]);

  useEffect(() => {
    if (previousOriginatingGuideId.current === null) {
      previousOriginatingGuideId.current = originatingGuideId;
      return;
    }
    if (previousOriginatingGuideId.current === originatingGuideId) {
      return;
    }
    previousOriginatingGuideId.current = originatingGuideId;
    setValue('memberIds', []);
    setValue('receivingGuideId', '');
    setValue('numericDetailByMemberId', {});
  }, [originatingGuideId, setValue]);

  useEffect(() => {
    if (teamHierarchy?.length) {
      void dispatch(ensureTeamAllViewable());
    } else if (
      teamHierarchy === null &&
      hierarchyLoadingStatus !== 'loading'
    ) {
      void dispatch(loadTeamHierarchy());
    }
  }, [dispatch, hierarchyLoadingStatus, teamHierarchy]);

  useEffect(() => {
    if (reasonsStatus === 'initial') {
      void dispatch(getMemberTransferReasons({ params: null }));
    }
  }, [dispatch, reasonsStatus]);

  useEffect(() => {
    if (!createBulkResult) {
      return;
    }
    dispatch(createBulkMemberTransferRequestSuccess(createBulkResult));
    toast(
      'Bulk transfer request approved and queued for assignment processing.',
      'success'
    );
    reset();
    setOpen(false);
  }, [createBulkResult, dispatch, reset, setOpen, toast]);

  const handleClose = () => {
    const close = () => {
      reset();
      setOpen(false);
    };
    if (formState.isDirty) {
      void handleUnsavedChanges(close);
    } else {
      close();
    }
  };

  const submit = (data: BulkMemberTransferRequestFormType) => {
    const transferDate = data.timestampDetail
      ? DateTime.fromFormat(
          data.timestampDetail,
          SCHEDULED_EVENT_DATE_FORMAT
        )
          .startOf('day')
          .toMillis()
      : DateTime.local().startOf('day').toMillis();
    void createBulkMemberTransferRequest({
      body: {
        memberIds: data.memberIds,
        originatingGuideId: data.originatingGuideId,
        receivingGuideId: data.receivingGuideId,
        transferReasonId: data.transferReasonId,
        stringDetail: data.stringDetail,
        numericDetail: data.numericDetail ?? undefined,
        numericDetailByMemberId:
          selectedTransferReason?.detailType === 'address'
            ? data.numericDetailByMemberId
            : undefined,
        commentToReceivingGuide: data.commentToReceivingGuide,
        timestampDetail: transferDate,
        creatorId: actingUser?.id,
        effectiveAt: transferDate,
      },
    });
  };

  return {
    form,
    formConfig,
    reasonsStatus,
    drawerStep,
    setDrawerStep,
    confirmationSummary,
    isSubmitting: Boolean(createBulkLoading) || formState.isSubmitting,
    handleClose,
    continueToConfirmation: (data: BulkMemberTransferRequestFormType) => {
      setConfirmationFormValues(data);
      setDrawerStep('confirmation');
    },
    submit,
    retryReasons: () =>
      dispatch(getMemberTransferReasons({ params: null })),
  };
}
